import React from 'react';
import { Mic, Sparkles, Send, ArrowRight } from 'lucide-react';

const HowItWorksPage = ({ onGetStarted }) => {
  const steps = [
    { num: 1, icon: Mic, title: 'Speak Your Expertise',
      desc: "Record a 2-minute voice note about your topic. Talk like you'd explain it to a client — conversationally, naturally, authentically. That's all Stilos needs.",
      details: [
        "Open Stilos on any device (phone, tablet, desktop)",
        "Hit the record button and speak about your topic",
        "Add optional keywords for SEO targeting",
        "Stop recording when you're done — typically 1-3 minutes"
      ]},
    { num: 2, icon: Sparkles, title: 'AI Transforms & Optimizes',
      desc: "Stilos transcribes your voice using Whisper AI, then structures your ideas into a full article with GPT-5.2. AEO/GEO optimization is applied automatically.",
      details: [
        "Whisper AI transcribes your speech with 99% accuracy",
        "GPT-5.2 structures, expands, and polishes your ideas",
        "FAQ sections are auto-generated for AI citation",
        "Real-time SEO and AEO/GEO scoring with improvement suggestions"
      ]},
    { num: 3, icon: Send, title: 'Publish Everywhere',
      desc: "Review, edit if needed, and publish directly to WordPress. Schedule for later or publish immediately. Your content calendar shows everything at a glance.",
      details: [
        "Edit in the built-in editor or publish as-is",
        "Direct publishing to WordPress (more platforms coming)",
        "Schedule posts with the visual content calendar",
        "Track performance with analytics dashboard"
      ]},
  ];

  return (
    <div>
      <section className="py-20 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <span className="font-label text-[var(--color-coral)] mb-3 inline-block">HOW IT WORKS</span>
          <h1 className="font-serif text-4xl md:text-5xl mb-4">From voice note to published<br />pillar in 3 steps</h1>
          <p className="text-base text-[var(--color-muted)] max-w-2xl mx-auto">
            No writing skills needed. No SEO expertise required. Just your voice, your expertise, and 2 minutes.
          </p>
        </div>
      </section>

      {steps.map((step, idx) => {
        const Icon = step.icon;
        return (
          <section key={idx} className={`py-20 px-6 ${idx % 2 !== 0 ? 'bg-white' : ''}`}>
            <div className="max-w-4xl mx-auto">
              <div className="grid md:grid-cols-2 gap-12 items-center">
                <div className={idx % 2 !== 0 ? 'md:order-2' : ''}>
                  <div className="flex items-center gap-3 mb-5">
                    <div className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold text-white bg-[var(--color-charcoal)]">{step.num}</div>
                    <span className="font-label text-[var(--color-coral)]">STEP {step.num}</span>
                  </div>
                  <h2 className="font-serif text-2xl md:text-3xl mb-4">{step.title}</h2>
                  <p className="text-sm text-[var(--color-muted)] leading-relaxed mb-6">{step.desc}</p>
                  <ul className="space-y-2.5">
                    {step.details.map((d, didx) => (
                      <li key={didx} className="flex items-start gap-3 text-sm">
                        <span className="text-[var(--color-coral)] mt-0.5 font-bold flex-shrink-0">{didx + 1}.</span>
                        <span>{d}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className={`card p-12 flex items-center justify-center bg-[var(--color-surface)] ${idx % 2 !== 0 ? 'md:order-1' : ''}`} style={{boxShadow: 'none', minHeight: 260}}>
                  <Icon className="w-20 h-20 text-[var(--color-coral)] opacity-15" />
                </div>
              </div>
            </div>
          </section>
        );
      })}

      <section className="py-20 px-6">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="font-serif text-3xl mb-4">Ready to try it?</h2>
          <p className="text-[var(--color-muted)] text-sm mb-8">Your first 3 articles are free. No credit card required.</p>
          <button onClick={onGetStarted} className="btn btn-primary text-base px-8 py-3">
            Record your first voice note <ArrowRight className="w-5 h-5" />
          </button>
        </div>
      </section>
    </div>
  );
};

export default HowItWorksPage;
